import { DataFileInterface, FileSourceEnum } from '@systemeio/file-manager'
import { Checkbox, FormInput, FormInputNumber, cents } from '@systemeio/ui'
import {
  ProductVariantInterface,
  ProductVariantsErrorsInterface,
} from 'modules/products/types/product-variant-interface'
import { ImageUploaderWithPreviewImage } from 'shared/components/file-uploader/image-uploader-with-preview-image'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import useMediaQuery from 'shared/hooks/use-media-query'
import { twJoin, twMerge } from 'tailwind-merge'

interface VariantProps {
  allowStock?: boolean
  variant: ProductVariantInterface
  setVariants: React.Dispatch<React.SetStateAction<ProductVariantInterface[]>>
  isDeleteAvailable: boolean | null
  variantsImages: DataFileInterface[]
  errors: { sku: string; stock: string; price: string }
  setErrors: React.Dispatch<React.SetStateAction<ProductVariantsErrorsInterface>>
}

function Variant({
  allowStock,
  variant,
  setVariants,
  isDeleteAvailable,
  variantsImages,
  errors,
  setErrors,
}: VariantProps) {
  const { t } = useLocoTranslation()
  const { isLG } = useMediaQuery()

  const handleChangeVariantSkuValue = (e: React.ChangeEvent<HTMLInputElement>) => {
    setVariants(prev => prev.map(v => (v.id === variant.id ? { ...v, sku: e.target.value } : v)))
    setErrors(prev => {
      const oldState = { ...prev }
      if (Object.keys(oldState).includes(variant.id)) {
        delete oldState[variant.id]
      }
      return oldState
    })
  }

  const handleChangeVariantPriceValue = (e: React.ChangeEvent<HTMLInputElement>) => {
    setVariants(prev =>
      prev.map(v =>
        v.id === variant.id
          ? {
              ...v,
              price: e.target.value
                ? cents(parseFloat(Number(e.target.value).toFixed(2)) * 100)
                : undefined,
            }
          : v,
      ),
    )
  }

  const handleChangeVariantStock = (newStock: number) => {
    setVariants(prev => prev.map(v => (v.id === variant.id ? { ...v, stock: newStock } : v)))
  }

  function setVariantImage(image?: DataFileInterface) {
    setVariants(prev => prev.map(v => (v.id === variant.id ? { ...v, image: image } : v)))
  }

  const handleChangeVariantSelect = (value: boolean) => {
    setVariants(prev => prev.map(v => (v.id === variant.id ? { ...v, selected: value } : v)))
  }

  let disabled = false
  if (isDeleteAvailable !== null) {
    disabled = isDeleteAvailable ? (variant.deleted ? true : false) : variant.deleted ? false : true
  }

  const hasStock = allowStock || (variant.stock ?? 0) > 0

  return (
    <>
      <div
        className={twMerge(
          'grid items-center',
          hasStock
            ? 'grid-cols-[18px_56px_minmax(15%,30%)_minmax(10%,18%)_minmax(10%,18%)_minmax(10%,20%)] gap-2'
            : 'grid-cols-[25px_70px_minmax(15%,45%)_minmax(10%,20%)_minmax(10%,20%)] gap-5',
        )}
      >
        <div>
          <Checkbox
            onChange={handleChangeVariantSelect}
            value={variant.selected ?? false}
            disabled={disabled}
          />
        </div>
        <ImageUploaderWithPreviewImage
          labelImageUploader=""
          aspectRatio={1}
          previewClassName="w-[60px] h-[60px]"
          value={variant.image}
          source={FileSourceEnum.Product}
          onSelect={setVariantImage}
          onRemove={() => setVariantImage(undefined)}
        />
        <div
          className={`text-text-primary customScroll overflow-x-auto whitespace-nowrap text-xl ${
            variant.deleted && 'line-through'
          }`}
        >
          {variant.productOptionValues?.map(e => e.value).join('/')}
        </div>
        <div className="ml-auto self-start">
          <FormInput
            value={variant.sku}
            placeholder={t('dashboard.products.sku_number.title')}
            onChange={handleChangeVariantSkuValue}
            maxLength={256}
            inputClassName={twJoin(isLG && 'w-full')}
            disabled={variant.deleted}
            error={errors.sku}
          />
        </div>
        <div className="self-start">
          <FormInput
            inputClassName={twJoin(isLG && 'w-full')}
            value={variant?.price ? variant?.price / 100 : variant?.price}
            placeholder={t('dashboard.products.variant_price.title')}
            onChange={handleChangeVariantPriceValue}
            type="number"
            maxLength={256}
            disabled={variant.deleted}
            error={errors.price}
          />
        </div>
        {hasStock && (
          <div className="self-start">
            <FormInputNumber
              value={variant?.stock || 0}
              placeholder={t('dashboard.products.variant_stock')}
              onChange={handleChangeVariantStock}
              maxLength={256}
              disabled={variant.deleted || !allowStock}
              error={errors.stock}
              type={'integer'}
            />
          </div>
        )}
      </div>
      <hr className="border-text-secondary -ml-6 -mr-6 mb-2 mt-2 border-t-[1.5px] border-solid opacity-40" />
    </>
  )
}

export default Variant
